import React, { useCallback, useState } from 'react'
import { Platform, TouchableOpacity, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { getParent, Instance } from 'mobx-state-tree'
import LinearGradient from 'react-native-linear-gradient'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'

import { ResourceIcon } from '../resource-icon'
import { SvgIcon } from '../svg-icon'
import { Text } from '../text'
import { color } from '../../theme/color'
import {
  iconFile,
  iconLibraryPathPlay,
  iconPathCompleted,
  iconPathQuiz,
  iconPathTopic,
  iconQuizLock,
} from '../../assets/icons'
import { Condition } from '@trivie/core/src/models/workflow/Condition'
import { Node } from '@trivie/core/src/models/workflow/Node'
import { useInterval } from '@trivie/core/src/utils/useInterval'
import { SvgXml } from 'react-native-svg'

export interface PathConditionProps {
  condition: Instance<typeof Condition>
  onPress: any
  completed?: boolean
  node: Instance<typeof Node>
}
export const PathCondition = observer((props: PathConditionProps) => {
  const { node, condition, completed, onPress, ...rest } = props

  const { t } = useTranslation()

  const [wait, setWait] = useState(
    t('Game.unlocksIn', {
      unlock: DateTime.fromISO(node.unlocks_at || '').toRelative(),
    }),
  )

  const [lock, setLock] = useState(node.locked())

  const label =
    completed && !lock
      ? `${condition?.object_type === 'Source' ? 'Source' : condition?.object_type} ${
          condition?.object_type === 'Source' ? 'Viewed' : 'Completed'
        }`
      : condition?.label

  const translateLabel = () => {
    if (label.includes('View Source')) {
      return t('Resources.viewResource')
    } else if (label.includes('Source Viewed')) {
      return t('Resources.sourceViewed')
    } else if (label.includes('Quiz Completed')) {
      return t('Path.quizCompleted')
    } else if (label.includes('Complete Quiz')) {
      return t('Path.completeQuiz')
    } else if (label.includes('Topic Completed')) {
      return t('Path.topicCompleted')
    } else if (label.includes('Complete Topic')) {
      return t('Path.completeTopic')
    } else if (label.includes('on Quiz')) {
      return t('Path.scoreXonQuiz', { pct: condition.value })
    } else if (label.includes('on Topic')) {
      return t('Path.scoreXonTopic', { pct: condition.value })
    }
    return label
  }

  const renderLeftIcon = (fill?: string) => {
    switch (condition?.object_type) {
      case 'Survey':
        return Platform.OS === 'web' ? (
          <svg viewBox="0 0 24 24" width="32" height="32">
            <path
              fill="#fff"
              d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2M8 17c-.55 0-1-.45-1-1v-5c0-.55.45-1 1-1s1 .45 1 1v5c0 .55-.45 1-1 1m4 0c-.55 0-1-.45-1-1V8c0-.55.45-1 1-1s1 .45 1 1v8c0 .55-.45 1-1 1m4 0c-.55 0-1-.45-1-1v-2c0-.55.45-1 1-1s1 .45 1 1v2c0 .55-.45 1-1 1"
            ></path>
          </svg>
        ) : (
          <SvgXml
            xml={`<svg width="32" height="32" viewBox="0 0 24 24">
                    <path
                      fill="#fff"
                      d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2M8 17c-.55 0-1-.45-1-1v-5c0-.55.45-1 1-1s1 .45 1 1v5c0 .55-.45 1-1 1m4 0c-.55 0-1-.45-1-1V8c0-.55.45-1 1-1s1 .45 1 1v8c0 .55-.45 1-1 1m4 0c-.55 0-1-.45-1-1v-2c0-.55.45-1 1-1s1 .45 1 1v2c0 .55-.45 1-1 1"
                    ></path>
                  </svg> `}
          />
        )
      case 'Source':
        return (
          <ResourceIcon extension={condition?.source?.source_file_type ?? 'url'} white={true} />
        )
      case 'Topic':
        return Platform.OS === 'web' ? (
          <svg fill={fill ?? '#fff'} width="32" height="32" viewBox="0 0 32 32">
            <path d="M26.6667 8.00004H16L13.3334 5.33337H5.33335C3.86669 5.33337 2.68002 6.53337 2.68002 8.00004L2.66669 24C2.66669 25.4667 3.86669 26.6667 5.33335 26.6667H26.6667C28.1334 26.6667 29.3334 25.4667 29.3334 24V10.6667C29.3334 9.20004 28.1334 8.00004 26.6667 8.00004ZM18.6667 21.3334H8.00002V18.6667H18.6667V21.3334ZM24 16H8.00002V13.3334H24V16Z" />
          </svg>
        ) : (
          <SvgIcon size={32} src={iconPathTopic} color={fill ?? color.white} />
        )
      case 'Quiz':
        if (Platform.OS === 'web') {
          return (
            <svg width="32" height="32" viewBox="0 0 32 32" fill={fill ?? '#fff'}>
              <g clipPath="url(#clip0_3999_425)">
                <path d="M14.73 27.0197V18.9447H9.90624C9.23527 18.9447 8.78191 18.1645 9.10833 17.5208L15.7818 3.53586C16.1989 2.61913 17.4864 2.95071 17.4864 3.98447V12.157H22.0925C22.7635 12.157 23.1987 12.9177 22.9086 13.5613L16.4527 27.4488C16.0175 28.385 14.73 28.0534 14.73 27.0197Z" />
              </g>
              <defs>
                <clipPath id="clip0_3999_425">
                  <rect width="32" height="32" fill="white" />
                </clipPath>
              </defs>
            </svg>
          )
        } else {
          return <SvgIcon size={32} src={iconPathQuiz} color={fill ?? color.white} />
        }
    }
  }

  const isLocked = () => {
    // if for some reason no content_user_id always mark as locked
    if (!node.metadata?.content_user_id) {
      return true
    }

    // if previous node is not complete, mark as locked
    const parent = getParent(node, 2) as any
    const currentIndex = parent.nodes.indexOf(node)

    // Find the previous non-Section Header node
    let previousNode: any = null
    for (let i = currentIndex - 1; i >= 0; i--) {
      const checkNode = parent.nodes[i]
      if (checkNode && checkNode.object_type !== 'NodeGroup') {
        previousNode = checkNode
        break
      }
    }

    // Check if previous content node is incomplete
    if (previousNode && !previousNode.is_completed) {
      return true
    }

    // if there is an unlocks_at datetime not in the past, mark as locked
    if (node.unlocks_at) {
      const diff = DateTime.now().diff(DateTime.fromISO(node.unlocks_at), 'milliseconds').toObject()
      const { milliseconds } = diff
      return (milliseconds || 0) <= 0
    }

    // otherwise unlock
    return false
  }

  useInterval(() => {
    if (lock && node.unlocks_at) {
      setWait(
        t('Game.unlocksIn', {
          unlock: DateTime.fromISO(node.unlocks_at || '').toRelative(),
        }),
      )
      // setWait(`Unlocks ${DateTime.fromISO(node.unlocks_at || '').toRelative()}`)
      setLock(isLocked())
    }
  }, 1000)

  const getSubtitle = useCallback(() => {
    const subtitle =
      node?.title ||
      condition?.topic?.topic_title ||
      condition?.quiz?.quiz_title ||
      condition?.source?.resource_title

    // if previous node is not complete or no content user id, return locked subtitle
    const parent = getParent(node, 2) as any
    const currentIndex = parent.nodes.indexOf(node)

    // Find the previous non-Section Header node
    let previousNode: any = null
    for (let i = currentIndex - 1; i >= 0; i--) {
      const checkNode = parent.nodes[i]
      if (checkNode && checkNode.object_type !== 'NodeGroup') {
        previousNode = checkNode
        break
      }
    }

    // Check if previous content node is incomplete
    if (!node.metadata?.content_user_id || (previousNode && !previousNode.is_completed)) {
      return t('Path.locked', { subtitle })
      // `Locked - ${subtitle}`
    }

    // otherwise if the node is locked due to a delay - update the subtitle with the
    // delay time (wait) information
    if (lock && node.unlocks_at && wait) {
      return `${wait} - ${subtitle}`
    }

    return subtitle?.trim()
  }, [wait, lock])

  return (
    <TouchableOpacity
      disabled={lock}
      style={{ ...STEP, backgroundColor: lock ? color.tint70 : '#fff' }}
      onPress={onPress}
      {...rest}
    >
      {completed ? (
        <View style={{ ...INDICATOR, backgroundColor: '#96D9FF' }}>{renderLeftIcon()}</View>
      ) : lock ? (
        <View
          style={{
            ...INDICATOR,
            borderColor: color.transparent,
            backgroundColor: color.transparent,
          }}
        >
          {condition?.object_type === 'Source' ? (
            Platform.OS === 'web' ? (
              <svg width="32" height="32" fill={color.shade20} viewBox="0 0 32 32">
                <g clipPath="url(#clip0_0_8)">
                  <path d="M19.4533 3.45333C18.9467 2.94667 18.2667 2.66667 17.56 2.66667H8C6.53333 2.66667 5.33333 3.86667 5.33333 5.33333V26.6667C5.33333 28.1333 6.52 29.3333 7.98666 29.3333H24C25.4667 29.3333 26.6667 28.1333 26.6667 26.6667V11.7733C26.6667 11.0667 26.3867 10.3867 25.88 9.89333L19.4533 3.45333ZM20 24H12C11.2667 24 10.6667 23.4 10.6667 22.6667C10.6667 21.9333 11.2667 21.3333 12 21.3333H20C20.7333 21.3333 21.3333 21.9333 21.3333 22.6667C21.3333 23.4 20.7333 24 20 24ZM20 18.6667H12C11.2667 18.6667 10.6667 18.0667 10.6667 17.3333C10.6667 16.6 11.2667 16 12 16H20C20.7333 16 21.3333 16.6 21.3333 17.3333C21.3333 18.0667 20.7333 18.6667 20 18.6667ZM17.3333 10.6667V4.66667L24.6667 12H18.6667C17.9333 12 17.3333 11.4 17.3333 10.6667Z" />
                </g>
                <defs>
                  <clipPath id="clip0_0_8">
                    <rect width="32" height="32" />
                  </clipPath>
                </defs>
              </svg>
            ) : (
              <SvgIcon color={color.shade20} size={32} src={iconFile} />
            )
          ) : (
            renderLeftIcon(color.shade20)
          )}
        </View>
      ) : !lock ? (
        <LinearGradient
          colors={['rgba(49, 119, 255, 1)', 'rgba(150, 124, 253, 1)']}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          style={INDICATOR}
        >
          {renderLeftIcon()}
        </LinearGradient>
      ) : null}
      <View style={ROW}>
        <View style={CONTENT}>
          <Text
            numberOfLines={1}
            style={{ color: color.shade70, marginBottom: 2 }}
            text={translateLabel()}
            disableLink
            variant="titleEmphasized"
          />
          <Text
            numberOfLines={1}
            style={{ color: color.shade50 }}
            text={getSubtitle()}
            disableLink
            variant="body2Medium"
          />
        </View>
      </View>
      <View style={{ ...ROW, flex: 0 }}>
        {completed ? (
          <SvgIcon size={42} src={iconPathCompleted} />
        ) : lock ? (
          <SvgIcon size={42} src={iconQuizLock} />
        ) : (
          <SvgIcon size={42} src={iconLibraryPathPlay} />
        )}
      </View>
    </TouchableOpacity>
  )
})

/**
 * Styling
 */
const STEP: ViewStyle = {
  height: 60,
  borderWidth: 1,
  borderColor: color.shade20,
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}
const INDICATOR: ViewStyle = {
  width: 60,
  height: 60,
  borderTopLeftRadius: 10,
  borderBottomLeftRadius: 10,
  borderWidth: 1,
  borderRightWidth: 0,
  left: -1,
  borderColor: color.shade10,
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
}
const CONTENT: ViewStyle = {
  flexDirection: 'column',
  display: 'flex',
  flex: 1,
  paddingLeft: 20,
  paddingRight: 20,
  justifyContent: 'center',
}
const ROW: ViewStyle = {
  flex: 1,
  paddingRight: Platform.OS === 'web' ? 60 : 20,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
}
